import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { AInput, ATextarea, ASelect } from "@/components/admin/AdminInputs";
import { PROPERTY_STATUSES, SOURCE_TYPES, EC_DECISIONS, RISK_LEVELS, PROPERTY_TYPES, TENURES, UW_GROUPS, RISK_KEYS, EMPTY_PROPERTY, computeCalc, gbp, pct } from "./crmPropertyShared";
import { STRATEGIES } from "./crmShared";

export default function CrmPropertyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_PROPERTY);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!id);

  useEffect(() => {
    if (!id) return;
    api.get(`/crm/properties/${id}`).then((r) => {
      const p = r.data;
      setForm({ ...EMPTY_PROPERTY, ...p, asking_price: p.asking_price ?? "", underwriting: p.underwriting || {}, risks: p.risks || {} });
    }).catch(() => { toast.error("Property not found"); navigate("/crm/properties"); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const setK = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setU = (k) => (v) => setForm((f) => ({ ...f, underwriting: { ...f.underwriting, [k]: v } }));
  const setR = (k) => (v) => setForm((f) => ({ ...f, risks: { ...f.risks, [k]: v } }));

  const calc = computeCalc(form.underwriting);

  const save = async () => {
    if (!form.address.trim()) return toast.error("Address / identifier is required");
    setBusy(true);
    try {
      const payload = { ...form, asking_price: form.asking_price === "" ? null : form.asking_price };
      if (id) { await api.put(`/crm/properties/${id}`, payload); toast.success("Property saved"); navigate(`/crm/properties/${id}`); }
      else { const { data } = await api.post("/crm/properties", payload); toast.success("Property created"); navigate(`/crm/properties/${data.id}`); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  if (loading) return <CrmShell title="Edit property"><div className="text-emerald-100/50 text-sm py-8">Loading…</div></CrmShell>;

  return (
    <CrmShell title={id ? "Edit property" : "New property"}>
      <button onClick={() => navigate(id ? `/crm/properties/${id}` : "/crm/properties")} className="flex items-center gap-1.5 text-sm text-emerald-100/70 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 space-y-5">
          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-white mb-3">Property identity</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><AInput label="Address / identifier" value={form.address} onChange={setK("address")} testId="prop-address" /></div>
              <AInput label="Town" value={form.town} onChange={setK("town")} />
              <AInput label="Postcode" value={form.postcode} onChange={setK("postcode")} />
              <AInput label="Area" value={form.area} onChange={setK("area")} testId="prop-area" />
              <ASelect label="Property type" value={form.property_type} onChange={setK("property_type")} options={PROPERTY_TYPES} />
              <AInput label="Bedrooms" type="number" value={form.bedrooms} onChange={setK("bedrooms")} />
              <ASelect label="Tenure" value={form.tenure} onChange={setK("tenure")} options={TENURES} />
              <AInput label="Approx floor area" value={form.floor_area} onChange={setK("floor_area")} />
            </div>
          </div>

          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-white mb-3">Source & status</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ASelect label="Source type" value={form.source_type} onChange={setK("source_type")} options={SOURCE_TYPES} testId="prop-source" />
              <AInput label="Source contact (name / reference)" value={form.source_contact} onChange={setK("source_contact")} />
              <ASelect label="Investment strategy" value={form.strategy} onChange={setK("strategy")} options={["", ...STRATEGIES]} testId="prop-strategy" />
              <AInput label="Asking price (£)" type="number" value={form.asking_price} onChange={setK("asking_price")} testId="prop-asking" />
              <ASelect label="Pipeline status" value={form.deal_status} onChange={setK("deal_status")} options={PROPERTY_STATUSES} testId="prop-status-select" />
            </div>
          </div>

          {UW_GROUPS.map((grp) => (
            <div key={grp.title} className="bg-[#0F2E23] rounded-xl border border-white/10 p-5">
              <h3 className="text-sm font-semibold text-white mb-3">Underwriting — {grp.title} <span className="text-emerald-100/40 font-normal">(£, leave blank if unknown)</span></h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {grp.fields.map(([k, lbl]) => <AInput key={k} label={lbl} type="number" value={form.underwriting[k] ?? ""} onChange={setU(k)} testId={`prop-uw-${k}`} />)}
              </div>
            </div>
          ))}

          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-white mb-3">Risk assessment</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {RISK_KEYS.map(([k, lbl]) => <ASelect key={k} label={lbl} value={form.risks[k] || "Not Assessed"} onChange={setR(k)} options={RISK_LEVELS} testId={`prop-risk-${k}`} />)}
            </div>
            <div className="mt-3"><ATextarea label="Risk notes" value={form.risk_notes} onChange={setK("risk_notes")} rows={2} /></div>
          </div>

          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-white mb-3">Emerald Crown decision</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ASelect label="Decision" value={form.ec_decision} onChange={setK("ec_decision")} options={["", ...EC_DECISIONS]} testId="prop-decision" />
              <AInput label="Decision reason" value={form.decision_reason} onChange={setK("decision_reason")} />
            </div>
            <div className="mt-3"><ATextarea label="Decision notes" value={form.decision_notes} onChange={setK("decision_notes")} rows={2} /></div>
          </div>

          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5"><ATextarea label="Notes" value={form.notes} onChange={setK("notes")} /></div>
          <button onClick={save} disabled={busy} data-testid="prop-save" className="flex items-center gap-1.5 bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-6 py-2.5 text-sm"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save property"}</button>
        </div>

        <div className="xl:sticky xl:top-20 self-start bg-[#0F2E23] rounded-xl border border-white/10 p-5" data-testid="prop-calc-preview">
          <h3 className="text-sm font-semibold text-white mb-3">Live underwriting</h3>
          <div className="space-y-2 text-sm">
            <Row label="Total acquisition" value={gbp(calc.total_acquisition)} />
            <Row label="Total refurb" value={gbp(calc.total_refurb)} />
            <Row label="Total project cost" value={gbp(calc.total_project_cost)} strong />
            <Row label="Target GDV" value={gbp(calc.target_gdv)} />
            <Row label="Estimated profit" value={gbp(calc.estimated_profit)} strong tone={calc.estimated_profit != null && calc.estimated_profit < 0 ? "neg" : "pos"} />
            <Row label="ROI (profit on cost)" value={pct(calc.roi)} strong />
            <Row label="Gross rental yield" value={pct(calc.gross_rental_yield)} />
          </div>
          <div className="mt-4 pt-3 border-t border-white/10 space-y-2">
            {[["Low", calc.scenarios.low], ["Target", calc.scenarios.target], ["High", calc.scenarios.high]].map(([lbl, s]) => (
              <div key={lbl} className="flex items-center justify-between text-xs">
                <span className="text-emerald-100/50">{lbl} GDV {gbp(s.gdv)}</span>
                <span className="text-white">{gbp(s.profit)} · {pct(s.roi)}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-emerald-100/40 mt-3">Estimates only — profit = GDV − total project cost.</p>
        </div>
      </div>
    </CrmShell>
  );
}

const Row = ({ label, value, strong, tone }) => (
  <div className="flex items-center justify-between">
    <span className="text-emerald-100/50">{label}</span>
    <span className={`${strong ? "font-semibold" : ""} ${tone === "neg" ? "text-red-300" : tone === "pos" ? "text-emerald-300" : "text-white"}`}>{value}</span>
  </div>
);
